import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

const faqs = [
  {
    question: 'How does AI for salons and spas help fill my appointment book?',
    answer: 'Your AI answers every call and responds to every online inquiry 24/7 — booking appointments directly into your calendar based on real-time availability. Whether a client calls during a busy Saturday or messages your website at midnight, they get an instant response and a confirmed booking. No more missed calls, no more empty slots.'
  },
  {
    question: 'Can the AI handle bookings for multiple stylists or treatment rooms?',
    answer: 'Yes. Your AI is configured to understand your team\'s individual schedules, specialties, and availability. Clients can book with a specific stylist or be matched to whoever is available — and the system prevents double bookings automatically.'
  },
  {
    question: 'Will the AI send appointment reminders to reduce no-shows?',
    answer: 'Absolutely. Automated reminders are one of the highest-impact features for beauty businesses. Your AI sends confirmation texts immediately after booking, a reminder 48 hours before the appointment, and a final reminder the morning of — dramatically reducing no-shows without any manual effort.'
  },
  {
    question: 'Can the AI answer questions about services, pricing, and availability?',
    answer: 'Yes. Your AI is trained on your full service menu, pricing, stylists, and policies. It can answer common questions about what\'s included in a treatment, how long services take, what to expect for a first visit, and more — giving clients the information they need to book with confidence.'
  },
  {
    question: 'Does this work for med spas and specialty beauty businesses?',
    answer: 'Yes. AI for beauty businesses is designed to handle the full range of salon and spa services — from haircuts and color to facials, massages, injectables, and laser treatments. Your AI is trained on your specific services and can handle the nuances of medical spa inquiries appropriately.'
  },
  {
    question: 'How quickly can I get set up?',
    answer: 'Most salons and spas are fully live within 48–72 hours. We handle the entire setup — training the AI on your services, team, and policies, connecting your booking calendar, and testing everything before launch. No technical knowledge required.'
  },
];

const painPoints = [
  {
    icon: 'ri-phone-line',
    title: 'Missed Calls During Busy Hours',
    description: 'When you\'re with a client, you can\'t answer the phone. Every missed call during peak hours is a potential booking — and a loyal customer — going to the salon down the street.'
  },
  {
    icon: 'ri-calendar-close-line',
    title: 'Double Bookings & Scheduling Chaos',
    description: 'Managing multiple stylists, treatment rooms, and service lengths manually is a recipe for errors. Double bookings and scheduling conflicts damage your reputation and stress your team.'
  },
  {
    icon: 'ri-time-line',
    title: 'Slow Responses to Client Inquiries',
    description: 'Clients expect instant responses — especially when they\'re ready to book. If you\'re not responding within minutes, they\'re booking with whoever replied first. Speed is everything in the beauty industry.'
  },
  {
    icon: 'ri-todo-line',
    title: 'Manual Appointment Management',
    description: 'Hours spent on the phone confirming bookings, sending reminders, and chasing no-shows is time you could spend with clients or growing your business. Manual management doesn\'t scale.'
  },
];

const solutions = [
  {
    icon: 'ri-phone-line',
    title: 'AI Phone Answering — Never Miss a Booking Call',
    description: 'Your AI answers every call in your salon or spa\'s name, 24/7. It books appointments, answers questions about services and pricing, and handles rescheduling requests — all without interrupting your team. AI for salons and spas means your front desk never goes unmanned.',
    link: '/ai-phone-answering-service',
    linkLabel: 'Learn about AI Phone Answering'
  },
  {
    icon: 'ri-chat-smile-3-line',
    title: 'AI Chatbot — Capture Website & Messaging Leads',
    description: 'Most clients research salons online before booking. Your AI chatbot engages website visitors 24/7 — answering questions, showcasing your services, and booking appointments instantly. AI for beauty businesses means every online visitor becomes a potential client.',
    link: '/services/ai-chatbot',
    linkLabel: 'Learn about AI Chatbot'
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Automated Scheduling — Fill Your Calendar Automatically',
    description: 'Clients book directly into your calendar based on real-time availability — for any stylist, any service, any time slot. The system prevents double bookings, sends instant confirmations, and keeps your schedule organized without any manual input.',
    link: '/ai-appointment-scheduling',
    linkLabel: 'Learn about Automated Scheduling'
  },
  {
    icon: 'ri-notification-3-line',
    title: 'Automated Reminders & Follow-Up',
    description: 'Confirmation texts go out immediately after booking. Reminders go out 48 hours and the morning of the appointment. After the visit, automated follow-ups request reviews and re-book clients for their next appointment — keeping your calendar full on autopilot.',
    link: '/services/ai-lead-follow-up',
    linkLabel: 'Learn about Automated Follow-Up'
  },
];

const benefits = [
  {
    icon: 'ri-calendar-2-line',
    stat: '40%',
    title: 'Fuller Calendar, More Revenue',
    description: 'Capture every call and online inquiry — including evenings and weekends — and convert them into confirmed appointments automatically. No more empty slots.'
  },
  {
    icon: 'ri-heart-line',
    stat: '5★',
    title: 'Better Client Experience',
    description: 'Clients get instant responses, easy booking, and timely reminders — creating a premium experience that builds loyalty and drives referrals.'
  },
  {
    icon: 'ri-user-follow-line',
    stat: '30%',
    title: 'Fewer No-Shows',
    description: 'Automated confirmation and reminder sequences dramatically reduce no-shows — protecting your revenue and keeping your team\'s time fully utilized.'
  },
  {
    icon: 'ri-time-line',
    stat: '10+ hrs',
    title: 'Time Saved Every Week',
    description: 'Stop spending hours on phone calls, manual scheduling, and reminder texts. AI for salons and spas handles the admin so your team can focus on clients.'
  },
];

const features = [
  {
    icon: 'ri-moon-line',
    title: '24/7 Booking Assistant',
    description: 'Clients can book appointments any time of day or night — even when your salon is closed. Your AI never sleeps, never takes a break, and never misses a booking opportunity. Wake up to a full calendar every morning.'
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Instant Client Responses',
    description: 'Every call, text, and website inquiry gets an immediate, personalized response. No hold times, no voicemail, no waiting until Monday morning. AI for beauty businesses means your clients always feel taken care of — instantly.'
  },
  {
    icon: 'ri-bell-line',
    title: 'Automated Appointment Reminders',
    description: 'A multi-step reminder sequence goes out automatically for every booking — confirmation at booking, reminder 48 hours before, and a final reminder the morning of. Clients show up prepared and on time, and your no-show rate drops significantly.'
  },
  {
    icon: 'ri-star-line',
    title: 'Post-Visit Review Requests',
    description: 'After every appointment, your AI sends a personalized follow-up thanking the client and requesting a Google review. More reviews mean better local search rankings and more new clients finding your salon or spa online.'
  },
  {
    icon: 'ri-repeat-2-line',
    title: 'Rebooking & Retention Automation',
    description: 'Your AI automatically reaches out to clients who haven\'t booked in 6–8 weeks with a personalized message about rebooking. Keep your regulars coming back consistently without any manual outreach.'
  },
  {
    icon: 'ri-user-settings-line',
    title: 'Multi-Stylist & Room Management',
    description: 'Configure your AI to manage bookings across multiple stylists, estheticians, or treatment rooms — each with their own availability, specialties, and service menu. No double bookings, no scheduling conflicts.'
  },
];

const steps = [
  {
    number: '01',
    title: 'We Set Up Your AI',
    description: 'We train your AI on your full service menu, team schedules, pricing, and policies. Setup takes 48–72 hours and we handle everything — no technical work required from you.'
  },
  {
    number: '02',
    title: 'AI Books & Manages',
    description: 'Your AI starts answering calls, responding to website inquiries, booking appointments, and sending reminders automatically. Every client interaction is handled professionally and instantly.'
  },
  {
    number: '03',
    title: 'You Focus on Clients',
    description: 'You and your team focus entirely on delivering exceptional service. Your calendar fills itself, reminders go out automatically, and follow-ups keep clients coming back — all without lifting a finger.'
  },
];

export default function AIForSalonsSpas() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  return (
    <>
      <SEO
        title="AI for Salons & Spas | Automated Booking & Client Management | Dino Nuggets"
        description="AI for salons and spas that books appointments 24/7, reduces no-shows, and responds instantly to every client inquiry. AI for beauty businesses that fills your calendar and saves your team hours every week."
        canonical="/industries/ai-for-salons-spas"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Automation for Salons and Spas',
              description: 'AI-powered appointment booking, client communication, automated reminders, and follow-up for hair salons, med spas, and beauty businesses.',
              provider: { '@type': 'Organization', name: 'Dino Nuggets' },
              areaServed: 'United States',
              keywords: 'AI for salons and spas, AI for beauty businesses, automated booking salon, AI appointment scheduling spa',
            },
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: faqs.map(f => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer },
              })),
            },
          },
        ]}
      />

      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Spring Sale Banner */}
        <div className="relative bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-white rounded-full translate-x-1/4 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-1/3 w-24 h-24 bg-white rounded-full translate-y-1/2"></div>
          </div>
          <div className="relative max-w-5xl mx-auto px-6 py-3.5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center sm:text-left">
            <div className="flex items-center gap-2.5">
              <span className="text-lg">🌱</span>
              <span className="font-bold text-base tracking-wide uppercase">Spring Sale</span>
              <span className="hidden sm:block w-px h-5 bg-white/40"></span>
            </div>
            <p className="text-sm sm:text-base font-medium text-white/95">
              <strong className="font-bold text-white">No setup fees</strong> on all automation packages through April 30th — save up to $500 on getting started.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="flex-shrink-0 bg-white text-emerald-600 hover:bg-emerald-50 px-4 py-1.5 rounded-full text-sm font-bold transition-all whitespace-nowrap cursor-pointer"
            >
              Claim Offer
            </button>
          </div>
        </div>

        {/* Hero */}
        <section className="relative pt-32 pb-24 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/readdy-assets/salon-spa-hero-bg-001.jpg"
              alt="AI Automation for Salons and Spas"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/65"></div>

          <div className="relative w-full max-w-4xl mx-auto text-center">
            <span className="inline-block bg-primary/20 border border-primary/40 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Built for Hair Salons, Med Spas &amp; Beauty Businesses
            </span>
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              AI Automation for Salons &amp; Spas
            </h1>
            <p className="text-xl text-white/85 mb-10 leading-relaxed max-w-3xl mx-auto">
              Book more appointments and respond instantly with AI-powered automation. AI for salons and spas handles your calls, online inquiries, scheduling, and client follow-up — so your team can focus entirely on delivering exceptional service.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Book a Free Demo
              </button>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-white/10 hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                See How It Works
              </button>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-secondary py-12 px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '24/7', label: 'Booking availability — clients book any time, day or night' },
              { value: '30%', label: 'Reduction in no-shows with automated reminders' },
              { value: '60 sec', label: 'Response time to every client inquiry' },
              { value: '48 hrs', label: 'From sign-up to fully automated' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm leading-snug">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">

            {/* Intro */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                Why Salons &amp; Spas Are Embracing AI
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Running a salon or spa means your hands are literally full — you can't stop mid-blowout to answer a booking call or respond to a DM. But clients expect instant responses, and if they don't get one, they book somewhere else. A dedicated <Link to="/services/ai-receptionist" className="text-primary hover:underline font-medium">AI receptionist</Link> bridges that gap entirely.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                AI for beauty businesses isn't about replacing the personal touch that makes your salon special — it's about making sure every potential client gets an immediate, professional response no matter when they reach out. Your AI handles the administrative side of your business so your team can focus on what they do best: making clients look and feel amazing.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Whether you run a boutique hair salon, a full-service day spa, or a med spa with multiple treatment providers, automation handles your booking flow, reduces no-shows, and keeps your calendar full — all without adding a single hour to your workday.
              </p>
            </div>

            {/* Pain Points */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                The Booking Problems Costing Your Salon Revenue
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                These are the four biggest operational challenges for salons and spas — and exactly what <Link to="/services/appointment-scheduling" className="text-primary hover:underline font-medium">automated appointment scheduling</Link> and AI communication is built to solve:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {painPoints.map((point) => (
                  <div key={point.title} className="bg-gray-50 p-6 rounded-xl flex gap-5">
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-red-50 rounded-lg">
                      <i className={`${point.icon} text-2xl text-red-400`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">{point.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{point.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Real Cost */}
            <div className="mb-20 bg-gradient-to-br from-gray-50 to-primary/5 p-10 rounded-2xl">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                What Missed Bookings Are Really Costing You
              </h2>
              <p className="text-gray-700 leading-relaxed mb-8">
                Every missed call, slow response, and no-show has a direct dollar cost. AI for salons and spas eliminates all three — turning every inquiry into a booked appointment and every booked appointment into a client who actually shows up.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { stat: '$75–$300+', color: 'text-red-400', label: 'Average value per salon or spa appointment', note: 'Each missed call or no-show is real revenue walking out the door — often multiple times per day.' },
                  { stat: '20–30%', color: 'text-amber-400', label: 'Typical no-show rate without reminders', note: 'Automated reminder sequences reduce no-shows by up to 30% — protecting your team\'s time and income.' },
                  { stat: '3–5x', color: 'text-primary', label: 'More Google reviews with automated requests', note: 'Post-visit review requests build your online reputation and drive new clients to your salon or spa.' },
                ].map((item) => (
                  <div key={item.stat} className="bg-white p-6 rounded-xl border border-gray-200">
                    <div className={`text-3xl font-bold ${item.color} mb-2`}>{item.stat}</div>
                    <div className="text-sm font-semibold text-gray-900 mb-2">{item.label}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{item.note}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Solution */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                How Dino Nuggets AI Works for Salons &amp; Spas
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Our complete AI for beauty businesses covers every touchpoint in your client journey — from first inquiry to rebooking:
              </p>
              <div className="space-y-6">
                {solutions.map((solution) => (
                  <div key={solution.title} className="bg-gray-50 p-6 rounded-xl flex gap-5">
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg">
                      <i className={`${solution.icon} text-2xl text-primary`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-xl text-gray-900 mb-2">{solution.title}</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">{solution.description}</p>
                      <Link
                        to={solution.link}
                        className="text-primary hover:underline text-sm font-medium inline-flex items-center gap-1 cursor-pointer"
                      >
                        {solution.linkLabel}
                        <i className="ri-arrow-right-line text-xs"></i>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                What AI for Salons &amp; Spas Delivers
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Real, measurable results that show up in your calendar, your revenue, and your client satisfaction:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {benefits.map((benefit) => (
                  <div key={benefit.title} className="border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0">
                        <i className={`${benefit.icon} text-2xl text-primary`}></i>
                      </div>
                      <div className="text-3xl font-bold text-primary">{benefit.stat}</div>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                Everything Included in Your Salon &amp; Spa AI
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                A complete automation system built specifically for AI for beauty businesses:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {features.map((feature) => (
                  <div key={feature.title} className="border border-gray-200 rounded-xl p-6 hover:border-primary/40 transition-colors">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-lg mb-4">
                      <i className={`${feature.icon} text-xl text-primary`}></i>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* How It Works */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                How It Works: 3 Simple Steps
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Getting started with AI for salons and spas is easier than you think. We handle everything.
              </p>
              <div className="space-y-6">
                {steps.map((step) => (
                  <div key={step.number} className="flex gap-6 items-start">
                    <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center bg-primary/10 rounded-xl">
                      <span className="text-primary font-bold text-lg">{step.number}</span>
                    </div>
                    <div className="flex-1 pt-1">
                      <h3 className="font-semibold text-gray-900 text-lg mb-1">{step.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Links */}
            <div className="mb-20 bg-gradient-to-br from-gray-50 to-primary/5 p-8 rounded-xl">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                Core Services Behind Your Salon &amp; Spa Automation
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Explore the individual services that power AI for salons and spas:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <Link
                  to="/ai-appointment-scheduling"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">AI Appointment Scheduling</h3>
                    <p className="text-sm text-gray-600">Automatic calendar booking for every service — with confirmations, reminders, and follow-ups sent to every client.</p>
                  </div>
                </Link>
                <Link
                  to="/services/ai-chatbot"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-chat-smile-3-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">AI Chatbot</h3>
                    <p className="text-sm text-gray-600">Capture and convert website visitors 24/7 — answer service questions and book appointments automatically.</p>
                  </div>
                </Link>
                <Link
                  to="/ai-phone-answering-service"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">AI Phone Answering</h3>
                    <p className="text-sm text-gray-600">Professional AI that answers every call in your salon or spa's name, 24/7 — booking appointments automatically.</p>
                  </div>
                </Link>
                <Link
                  to="/services/ai-lead-follow-up"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-send-plane-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">Automated Follow-Up</h3>
                    <p className="text-sm text-gray-600">Post-visit review requests, rebooking reminders, and re-engagement campaigns that keep clients coming back.</p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Other Industries */}
            <div className="mb-20">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                See How We Help Other Service Businesses
              </h2>
              <div className="flex flex-wrap gap-3">
                <Link to="/ai-for-contractors" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Contractors</Link>
                <Link to="/ai-for-roofers" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Roofers</Link>
                <Link to="/industries/ai-for-painters" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Painters</Link>
                <Link to="/industries/ai-for-appliance-repair" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Appliance Repair</Link>
                <Link to="/industries/ai-for-home-services" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Home Services</Link>
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <i className={`ri-arrow-down-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-4 pt-2">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Final CTA */}
            <div className="bg-gradient-to-br from-primary to-secondary text-white p-12 rounded-2xl text-center">
              <h2 className="font-display text-3xl font-bold mb-4">
                Let AI Manage Your Bookings
              </h2>
              <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
                Stop losing clients to missed calls and slow responses. AI for salons and spas fills your calendar, reduces no-shows, and keeps clients coming back — all on autopilot. Get set up in 48 hours.
              </p>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Get Started Today
              </button>
            </div>

          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />
        <ServiceModal isOpen={isServiceModalOpen} onClose={() => setIsServiceModalOpen(false)} title={selectedService} />
        <IndustriesModal isOpen={isIndustriesModalOpen} onClose={() => setIsIndustriesModalOpen(false)} />
        <HowItWorksModal isOpen={isHowItWorksModalOpen} onClose={() => setIsHowItWorksModalOpen(false)} />
        <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
      </div>
    </>
  );
}
